import { useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { tracker } from '../../../tracker';
import { PlaybackEndedEvent } from '../../../tracker/events/playback-ended';
import { HttpClientError } from '../../../controllers/http-client/http-client';
import { bookmarksKey } from '../../../queries/ats/me/bookmarks/keys';
import { Bookmark } from '../../../queries/ats/me/bookmarks/types';
import { continueWatchingKey } from '../../../queries/ats/me/keys';
import {
  play,
  PlayData,
  PlayOptions,
  reportConsumption as report,
  startSession,
  stopConsumption as stop,
} from '../../../queries/ats/playback/api';
import { HttpPlaybackError } from '../../../queries/ats/playback/error';
import { findPlaybackOption } from '../../../queries/ats/playback/utils';
import { PlayablePlaybackOption } from '../../../queries/ats/titles/types/playback-option';
import { PlayableTitle } from '../../../queries/ats/titles/types/title';
import { queryClient } from '../../../queries/client';

export function usePlay(options: PlayOptions) {
  const [data, setData] = useState<PlayData>();
  const [title, setTitle] = useState<PlayableTitle>();
  const [playbackOption, setPlaybackOption] = useState<PlayablePlaybackOption>();
  const [bookmark, setBookmark] = useState<Bookmark>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const updateData = useCallback((partial: Partial<PlayData>) => {
    return setData((prev) => {
      if (!prev) {
        return prev;
      }

      return { ...prev, ...partial };
    });
  }, []);

  const reportConsumption = useCallback(
    async (position: number) => {
      if (data === undefined) {
        console.error('Cannot report consumption without session id.');
        return;
      }

      try {
        // We don't want to invalidate bookmarks here, as we subscribe to bookmarks
        // in the player, which would casue the player to re-fetch bookmarks.
        await report({ sessionId: data.sessionId, position });
      } catch (err) {
        if (!title) {
          throw err;
        }

        if (err instanceof HttpClientError && err.status === 404) {
          const sessionId = await reStartSession(title, {
            platformTitleId: options.platformTitleId,
            providerIdentifier: options.providerIdentifier,
          });
          await report({ sessionId, position });
          updateData({ sessionId });
          return;
        }

        if (err instanceof HttpClientError) {
          setError(new HttpPlaybackError(err));
          return;
        }

        throw err;
      }
    },
    [data, options.platformTitleId, options.providerIdentifier, title, updateData],
  );

  const stopConsumption = useCallback(
    async (position: number) => {
      if (data === undefined) {
        console.error('Cannot stop consumption without session id.');
        return;
      }

      if (title !== undefined && playbackOption !== undefined) {
        tracker.track(new PlaybackEndedEvent(title, playbackOption, position, options.isTrailer));
      }

      await stop({ sessionId: data.sessionId, position });
      await queryClient.invalidateQueries(bookmarksKey());
      await queryClient.invalidateQueries(continueWatchingKey());
    },
    [data, options.isTrailer, playbackOption, title],
  );

  const fetchPlay = useCallback(
    async (signal: AbortSignal) => {
      try {
        setIsLoading(true);

        const response = await play({
          masterId: options.masterId,
          platformTitleId: options.platformTitleId,
          providerIdentifier: options.providerIdentifier,
          isTrailer: options.isTrailer,
          startSession: options.startSession,
          signal: signal,
        });

        // If the signal was aborted, we don't want to update the state.
        if (signal.aborted) {
          return;
        }

        // Since we are still on React 17, state updates are not
        // automatically batched in async functions. We need to wrap
        // the state updates in a batchedUpdates call.
        unstable_batchedUpdates(() => {
          setData(response[0]);
          setTitle(response[1]);
          setPlaybackOption(response[2]);
          setBookmark(response[3]);
        });
      } catch (err) {
        if (err instanceof HttpClientError) {
          setError(new HttpPlaybackError(err));
          return;
        }

        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [options.platformTitleId, options.providerIdentifier, options.isTrailer, options.startSession, options.masterId],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchPlay(controller.signal);
    return () => controller.abort('use-play: Signal aborted because useEffect ran the cleanup function.');
  }, [fetchPlay]);

  if (error) {
    throw error;
  }

  return {
    data: { data, title, playbackOption, bookmark },
    isLoading,
    reportConsumption,
    stopConsumption,
  };
}

async function reStartSession(
  title: PlayableTitle,
  options: Pick<PlayOptions, 'platformTitleId' | 'providerIdentifier'>,
): Promise<string> {
  const playbackOption = findPlaybackOption(title, options);

  if (!playbackOption) {
    throw new Error('Playback option not found');
  }

  return startSession({
    duration: 0,
    platformTitleId: playbackOption.platformTitleId,
    providerIdentifier: playbackOption.providerIdentifier,
  });
}

declare global {
  interface AbortController {
    abort(reason: string): void;
  }
}
